import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ChargePointModal from '../../Components/Modals/ChargePointModal';
import Button from '../../Components/Utils/Button';
import IfRender from '../../Components/Utils/IfRender';
import ReactTable from '../../Components/Utils/ReactTable';

import {
  useRequestAllChargingPointsQuery,
  useChargingPointsCountQuery
} from '../../redux/api/chargingPoints';
import {
  useRequestAllOrganizationsQuery,
  useRequestAllOrganizationsForUserIdQuery
} from '../../redux/api/organizations';
import { selectAuthData } from '../../redux/selectors/auth';

import { dateFormatting } from '../../Utils';

import Page from '../../Components/Page';
import './style.scss';
import { selectUserType } from '../../redux/selectors/userType';
import { chargersFilter } from '../../QueryFilters/queries';
import LoadingAnimation from '../../Components/Utils/BateryChargeAnimation';

function ChargingPoints() {
  const { user: { id: userId = '' } = {} } = useSelector(selectAuthData);
  const { isAdmin, isSuperAdmin } = useSelector(selectUserType);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Create filter with pagination parameters
  const params = useMemo(() => {
    // Get the base filter from chargersFilter
    const baseFilter = chargersFilter();

    // Update pagination parameters
    baseFilter.limit = itemsPerPage;
    baseFilter.skip = page * itemsPerPage;

    return {
      filter: JSON.stringify(baseFilter)
    };
  }, [itemsPerPage, page]);

  // Fetch charging points data
  const { isFetching, data, refetch: refetchCP } = useRequestAllChargingPointsQuery(params);

  const chargers = useMemo(() => {
    if (!data) return [];
    if (data.list && Array.isArray(data.list)) {
      return data.list;
    }
  }, [data]);

  // Create where filter for count
  const countParams = useMemo(() => {
    const baseFilter = chargersFilter();
    return {
      where: JSON.stringify(baseFilter.where || {})
    };
  }, []);

  // Fetch total count using Redux query
  const { data: countData } = useChargingPointsCountQuery(countParams);

  // Get total count from countData or fallback to array length
  const totalCount = useMemo(() => {
    if (countData?.count) {
      return countData.count;
    }

    // If we have the full data array, use its length
    if (Array.isArray(data)) {
      return data.length;
    }

    return 0;
  }, [countData, data]);

  // Calculate page count
  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / itemsPerPage));
  }, [totalCount, itemsPerPage]);

  const filter = JSON.stringify({
    include: ['organizationProperties']
  });

  const { data: organizations } = isSuperAdmin
    ? useRequestAllOrganizationsQuery({
        filter: filter
      })
    : useRequestAllOrganizationsForUserIdQuery({
        id: userId,
        filter: filter
      });
  const { t } = useTranslation();

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setItemsPerPage(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const openEditModal = (modalData = {}) => {
    //Sets modal data to the selected table row
    setModalData(modalData);
    setIsModalOpen(true);
  };

  const toggleAddModal = () => {
    setIsModalOpen(!isModalOpen);
    setModalData({});
  };

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        accessor: '_id',
        disableSortBy: true
      },
      {
        Header: t('name'),
        accessor: 'name'
      },
      {
        Header: 'Slug',
        accessor: 'slug'
      },
      {
        Header: t('organization'),
        accessor: 'organization',
        Cell: ({ cell }) => {
          return cell.row.values.organization?.name || '-';
        }
      },
      {
        Header: t('currency'),
        accessor: 'chargePointConfiguration',
        Cell: ({ cell }) => cell.row.values?.chargePointConfiguration?.currency || '-'
      },
      {
        Header: t('Price/KW'),
        accessor: 'prices',
        Cell: ({ cell }) =>
          cell.row.values?.prices && cell.row.values.prices.length > 0
            ? cell.row.values.prices[cell.row.values.prices.length - 1].price * 10
            : '-'
      },
      {
        Header: t('connectors'),
        accessor: 'chargePointConnectors',
        Cell: ({ cell }) => cell.row.values.chargePointConnectors?.length || 0
      },
      {
        Header: t('public'),
        accessor: 'public',
        Cell: ({ cell }) => (cell.row.values.public ? 'Yes' : 'No')
      },
      {
        Header: t('model'),
        accessor: 'chargePointConfiguration.modelName',
        Cell: ({ cell }) => cell.row.values.chargePointConfiguration?.modelName || '-'
      },
      {
        Header: 'Max kW Authorize',
        accessor: 'chargePointConfiguration.wattsAuthorize',
        Cell: ({ cell }) => {
          const watts = cell.row.values.chargePointConfiguration?.wattsAuthorize;
          return watts ? watts / 1000 : '-';
        }
      },
      {
        Header: t('max_power'),
        accessor: 'chargePointConfiguration.maxPower',
        Cell: ({ cell }) => cell.row.values.chargePointConfiguration?.maxPower || '-'
      },
      {
        Header: t('vendor'),
        accessor: 'chargePointConfiguration.vendor',
        Cell: ({ cell }) => cell.row.values.chargePointConfiguration?.vendor || '-'
      },
      {
        Header: t('firmware'),
        accessor: 'chargePointConfiguration.firmwareVersion',
        Cell: ({ cell }) => cell.row.values.chargePointConfiguration?.firmwareVersion || '-'
      },
      {
        Header: t('serial_number'),
        accessor: 'chargePointConfiguration.serialNumber',
        Cell: ({ cell }) => cell.row.values.chargePointConfiguration?.serialNumber || '-'
      },
      {
        Header: t('created_at'),
        accessor: 'createdAt',
        Cell: ({ cell }) => dateFormatting(cell.row.values.createdAt)
      }
    ];
    if (isSuperAdmin || isAdmin) {
      defaultColumns.push({
        Header: t('actions'),
        accessor: 'actions',
        className: 'table-action-cell',
        Cell: ({ cell }) => (
          <Button
            label={t('edit')}
            className='button button__alfa'
            onClick={() => {
              openEditModal(cell.row.original);
            }}
          />
        )
      });
    }
    return defaultColumns;
  }, [chargers, organizations, t]);

  const headerItems = (
    <IfRender condition={isSuperAdmin || isAdmin}>
      <Button className='button button__alfa' onClick={toggleAddModal}>
        {t('add_charging_point')}
      </Button>
    </IfRender>
  );
  return (
    <Page title={t('charging_points')} customHeaderItems={headerItems}>
      <>
        {isModalOpen && (
          <ChargePointModal
            authenticatedUserId={userId}
            modalData={modalData}
            refetch={refetchCP}
            organizations={organizations || []}
            toggleModal={toggleAddModal}
            cacheParams={params}
          />
        )}
        <div>
          {chargers.length > 0 ? (
            <ReactTable
              hiddenColumns={['_id', 'currency']}
              loading={isFetching}
              data={chargers}
              columns={columns}
              setControlledPageSize={setItemsPerPage}
              setControlledPageIndex={setPage}
              controlledPageCount={pageCount}
              manualPagination={true}
              initialState={{
                pageIndex: page,
                pageSize: itemsPerPage
              }}
            />
          ) : (
            <div className='no-data-message'>
              {isFetching ? <LoadingAnimation /> : 'No charging points found'}
            </div>
          )}
        </div>
      </>
    </Page>
  );
}

export default ChargingPoints;
